import React from 'react'

import { Button } from '@wartek-id/button-v1'
import { Icon } from '@wartek-id/icon-v2'
import { Text } from '@wartek-id/text-v1'

import {
  ABSENT_BADGE_CONFIG,
  ABSENT_NOTE_MESSAGE,
  BADGE_CONFIG_BY_STATE,
  MIN_SENT_TO_BKN_COUNT,
  WIB_SUFFIX,
} from 'app/KinerjaHarian/constants'
import { KinerjaHarianNoteCard } from 'app/KinerjaHarian/components/logs/KinerjaHarianNoteCard'
import { KinerjaHarianStatusBadge } from 'app/KinerjaHarian/components/logs/KinerjaHarianStatusBadge'

import type { KinerjaHarianLog, KinerjaHarianNote } from 'types/kinerja-harian'

function formatDateLabel(entryDate: string) {
  const [year, month, day] = entryDate.split('-').map(Number)
  const formattedDate = new Date(year, month - 1, day).toLocaleDateString(
    'id-ID',
    {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    }
  )

  return formattedDate.replace(/\b\w/g, (char) => char.toUpperCase())
}

function formatHourLabel(hour?: string) {
  if (!hour) {
    return '-'
  }

  return `${hour} ${WIB_SUFFIX}`
}

interface KinerjaHarianListItemProps {
  isExpanded: boolean
  item: KinerjaHarianLog
  onDelete: (itemId: string) => void
  onEdit: (itemId: string) => void
  onToggleExpanded: (itemId: string) => void
}

export function KinerjaHarianListItem({
  isExpanded,
  item,
  onDelete,
  onEdit,
  onToggleExpanded,
}: KinerjaHarianListItemProps) {
  const dateLabel = formatDateLabel(item.entryDate)
  const notes: KinerjaHarianNote[] = item.contents.map((content, index) => ({
    id: `${item.entryDate}-${content.contentId}-${index}`,
    activity: content.contentDescription || `Konten ${index + 1}`,
    startTime: formatHourLabel(content.startHour),
    endTime: formatHourLabel(content.endHour),
  }))
  const badgeConfig =
    BADGE_CONFIG_BY_STATE?.[item.state] ?? BADGE_CONFIG_BY_STATE['SUBMITTED']

  // Disable edit/delete if the log has been processed or already sent to BKN
  const isEditDisabled = item.sentToBknCount >= MIN_SENT_TO_BKN_COUNT
  const isDeleteDisabled = item.sentToBknCount >= MIN_SENT_TO_BKN_COUNT

  return (
    <section className="rounded-lg border border-[#C9CBCF] bg-[#F9FAFB]">
      <div className="flex items-center gap-4 px-5 py-4">
        <div className="flex items-center flex-1 min-w-0 gap-3">
          <Icon color="informational">calendar_today</Icon>
          <Text variant="body" className="truncate">
            {dateLabel}
          </Text>
        </div>

        <div className="flex items-center gap-3">
          {item.absent && (
            <KinerjaHarianStatusBadge tone={ABSENT_BADGE_CONFIG.tone}>
              {ABSENT_BADGE_CONFIG.label}
            </KinerjaHarianStatusBadge>
          )}

          <KinerjaHarianStatusBadge tone={badgeConfig.tone}>
            {badgeConfig.label}
          </KinerjaHarianStatusBadge>
        </div>

        <div className="flex items-center gap-2">
          {!isEditDisabled && (
            <Button
              iconOnly
              variant="ghost"
              aria-label={`Ubah catatan untuk ${dateLabel}`}
              onClick={() => onEdit(item.entryDate)}
            >
              <Icon>mode_edit_outline</Icon>
            </Button>
          )}
          <Button
            iconOnly
            variant="ghost"
            aria-label={
              isExpanded
                ? `Tutup rincian ${dateLabel}`
                : `Buka rincian ${dateLabel}`
            }
            onClick={() => onToggleExpanded(item.entryDate)}
          >
            <Icon>{isExpanded ? 'expand_less' : 'expand_more'}</Icon>
          </Button>
          {!isDeleteDisabled && (
            <Button
              iconOnly
              variant="ghost"
              aria-label={`Hapus catatan untuk ${dateLabel}`}
              onClick={() => {
                onDelete(item.entryDate)
              }}
            >
              <Icon>delete_outline</Icon>
            </Button>
          )}
        </div>
      </div>

      {isExpanded && (
        <div className="px-5 py-4 border-t border-gray-30">
          <div className="space-y-4">
            <Text>Catatan Kinerja:</Text>

            {item.absent && (
              <div className="px-4 py-5 border rounded-lg border-red-40 bg-surface-critical-subdued">
                <Text color="critical">{ABSENT_NOTE_MESSAGE}</Text>
              </div>
            )}

            {!item.absent && (
              <>
                {notes.length > 0 ? (
                  notes.map((note) => (
                    <KinerjaHarianNoteCard key={note.id} note={note} />
                  ))
                ) : (
                  <div className="px-4 py-5 border border-dashed rounded-lg border-gray-30 bg-gray-10">
                    <Text color="subdued">
                      Belum ada catatan kinerja untuk tanggal ini.
                    </Text>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}
    </section>
  )
}
